/* eslint-disable no-underscore-dangle */
import React from 'react';
import {
  Formik,
  Field,
  FieldArray,
  ArrayHelpers,
} from 'formik';
import * as Yup from 'yup';
import {
  components,
} from 'react-select';

import {
  Icon,
  Button,
  TextField,
  SelectField,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
} from 'web-styled';

import { countTotal, FormData } from '../utils';
import * as S from '../styled';

const SingleValue = ({
  children,
  selectProps,
  ...rest
}: SingleValueProps<ColourOption>) => (
  <components.SingleValue {...selectProps} {...rest}>
    <Flex
      align="center"
      justify="space-between"
      spacing={8}
    >
      <span>{children}</span>
      {selectProps.value?.rest?.email && (
        <Text color="#c1c1c1">
          {selectProps.value?.rest?.email}
        </Text>
      )}
    </Flex>
  </components.SingleValue>
);

const Option = ({
  data,
  label,
  selectProps,
  ...restProps
}: any) => (
  <components.Option {...selectProps} {...restProps}>
    <Flex
      align="center"
      justify="space-between"
      spacing={8}
    >
      <span>{label}</span>
      {data.rest?.email && (
        <Text color={restProps.isSelected ? '' : 'primary'}>
          {data.rest.email}
        </Text>
      )}
    </Flex>
  </components.Option>
);

const validationSchema = () => Yup.lazy(
  (values: FormData) => Yup.object().shape({
    ...(values.custom_email
      ? {
        client_id: Yup.string()
          .email('Email must be a valid email')
          .required('Client is required'),
        custom_name: Yup.string()
          .required('Name is required'),
      }
      : {
        client_id: Yup.string()
          .required('Client is required'),
      }),

    items: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required('Required'),
          price: Yup.string().required('Required'),
          quantity: Yup.string().required('Required'),
        }),
      )
      .required('Must have items')
      .min(1, 'Minimum of 1 item'),
  }),
);

type Props = {
  initialValues: FormData;
  clientOptions: any[],
  productOptions: any[],
  onSubmit: { (values: FormData, actions: any): void };
  onCancel: { (): void };
};

const BasicInfo = ({
  initialValues,
  clientOptions,
  productOptions,
  onSubmit,
  onCancel,
}: Props) => (
  <Formik
    validationSchema={validationSchema}
    initialValues={initialValues}
    onSubmit={onSubmit}
  >
    {(formikProps) => (
      <Flex
        column
        as="form"
        width="100%"
        spacing={15}
        onSubmit={formikProps.handleSubmit}
      >
        <Flex
          column
          width="100%"
          spacing={10}
        >
          <H200>Client</H200>
          <Field
            name="client_id"
            placeholder="Search a client"
            options={clientOptions}
            creatable
            formatCreateLabel={(userInput: string) => (
              <span>
                Add
                {' '}
                <b>{userInput}</b>
              </span>
            )}
            component={SelectField}
            components={{
              Option,
              SingleValue,
            }}
            styles={{
              singleValue: (base) => ({
                ...base,
                width: 'calc(100% - 20px)',
              }),
            }}
            onChange={(option: any) => {
              if (option.__isNew__) {
                formikProps.setFieldValue('custom_email', option.value);
              } else {
                formikProps.setFieldValue('custom_name', '');
                formikProps.setFieldValue('custom_email', '');
              }
            }}
          />
          {formikProps.values.custom_email && (
            <Field
              name="custom_name"
              label="Name"
              placeholder="e.g Smith"
              component={TextField}
            />
          )}
        </Flex>
        <Flex
          column
          width="100%"
          spacing={10}
        >
          <H200>Items</H200>
          <FieldArray
            name="items"
          >
            {(arrayHelpers: ArrayHelpers) => (
              <Flex
                column
                spacing={12}
                width="70%"
              >
                <Flex
                  column
                  spacing={8}
                  width="100%"
                >
                  {formikProps.values.items.map((item, index) => (
                    <Flex
                      key={String(index)}
                      spacing={15}
                      width="100%"
                    >
                      <Field
                        name={`items.${index}.name`}
                        label={index === 0 ? 'Item' : ''}
                        creatable
                        options={productOptions}
                        placeholder="Ex: Training Session"
                        component={SelectField}
                        formatCreateLabel={(userInput: string) => (
                          <Text color="primary">
                            Add
                            {' '}
                            <b>{userInput}</b>
                          </Text>
                        )}
                        onChange={(option: any) => {
                          formikProps.setFieldValue(`items.${index}.name`, option?.label || '');
                          formikProps.setFieldValue(`items.${index}.quantity`, 1);

                          if (option?.rest?.amount) {
                            formikProps.setFieldValue(`items.${index}.price`, option.rest.amount);
                          }

                          if (option?.value && !option.__isNew__) {
                            formikProps.setFieldValue(`items.${index}.product_id`, option.value);
                          }
                        }}
                      />
                      <Field
                        name={`items.${index}.quantity`}
                        label={index === 0 ? 'Quantity' : ''}
                        type="number"
                        width="70px"
                        className="input-text-center"
                        component={TextField}
                      />
                      <S.AmountWrapper>
                        <Field
                          name={`items.${index}.price`}
                          label={index === 0 ? 'Price' : ''}
                          type="number"
                          placeholder="XX.XX"
                          component={TextField}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            top: index === 0 ? '41px' : '13px',
                            left: '12px',
                          }}
                        >
                          $
                        </span>
                      </S.AmountWrapper>

                      {(index > 0 || (item.name || item.price)) && (
                        <S.Remove
                          type="button"
                          style={{
                            top: index === 0 ? '41px' : '13px',
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (formikProps.values.items.length > 1) {
                              arrayHelpers.remove(index);
                            } else {
                              formikProps.setFieldValue('items.0.name', '');
                              formikProps.setFieldValue('items.0.price', '');
                              formikProps.setFieldValue('items.0.quantity', 1);
                              formikProps.setFieldValue('items.0.product_id', undefined);
                            }
                          }}
                        >
                          <Icon
                            type="fas fa-times"
                            color="danger"
                          />
                        </S.Remove>
                      )}
                    </Flex>
                  ))}
                </Flex>
                <hr />
                <Flex
                  width="100%"
                  align="center"
                  justify="space-between"
                >
                  <Flex
                    clickable
                    onClick={() => {
                      arrayHelpers.push({
                        name: '',
                        price: '',
                        quantity: '1',
                        product_id: '',
                      });
                    }}
                  >
                    <Text color="primary">
                      + Add item
                    </Text>
                  </Flex>
                  <Flex spacing={15} align="center">
                    <Flex width="45px" justify="center">
                      <Text>
                        Total
                      </Text>
                    </Flex>
                    <Flex width="80px" justify="center">
                      <Text weight="bold">
                        {'$ '}
                        {countTotal(formikProps.values.items)}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            )}
          </FieldArray>
        </Flex>
        <Flex
          column
          width="100%"
          spacing={10}
        >
          <H200>Memo</H200>
          <Field
            name="description"
            textarea
            minRows={4}
            placeholder="Enter an optional memo for this invoice"
            component={TextField}
          />
        </Flex>
        <Flex align="center" spacing={5}>
          <Text>
            Payment is due after
          </Text>
          <Field
            name="days_until_due"
            type="number"
            width="42px"
            align="center"
            className="input-text-center"
            placeholder="1"
            component={TextField}
          />
          <Text>
            days
          </Text>
        </Flex>

        <Flex
          width="100%"
          justify="space-between"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="danger"
            isDisabled={formikProps.isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            ghost
            width="140px"
            type="submit"
            spacing="large"
            appearance="primary"
            isLoading={formikProps.isSubmitting}
            isDisabled={formikProps.isSubmitting}
          >
            Review
          </Button>
        </Flex>
      </Flex>
    )}
  </Formik>
);

export default BasicInfo;
